import { ref } from 'vue'
import { createRouter, createWebHashHistory, RouteRecordRaw } from 'vue-router'
import AdenLocalStorage from '@/consts/storage.util'
import StorageUtil from '@/consts/storage.const'

export interface BackendRouter {
  name?: string
  path?: string
  hidden?: boolean
  redirect?: string
  component?: string
  query?: string
  meta?: {
    title?: string
    icon?: string
    noCache?: boolean
    link?: string
  }
  children?: BackendRouter[]
}

export interface AdminMenuItem {
  title: string
  router: string
  icon: string
  children?: AdminMenuItem[]
}

const routePathAliases: Record<string, string> = {
  '/users/list': '/users/accounts',
  '/order/orders': '/orders/orders',
  '/coupon/list': '/coupons/list',
  '/ops/popups': '/operations/banners',
  '/ops/notices': '/operations/notices',
  '/user': '/system/user',
  '/role': '/system/role',
  '/dept': '/system/dept',
  '/menu': '/system/menu',
  '/notice': '/operations/notices',
  '/system/user/index': '/system/user',
  '/system/role/index': '/system/role',
  '/system/dept/index': '/system/dept',
  '/system/menu/index': '/system/menu',
  '/system/notice/index': '/operations/notices',
  '/feedback': '/operations/work-orders',
  '/feedback/list': '/operations/work-orders',
  '/basic/feedback': '/operations/work-orders',
  '/basic/feedback/list': '/operations/work-orders',
}

const pageKeyAliases: Record<string, string> = {
  '/users/list': 'users.accounts',
  '/order/orders': 'orders.orders',
  '/coupon/list': 'coupons.list',
  '/ops/popups': 'operations.banners',
  '/ops/notices': 'operations.notices',
  '/ai/config': 'ai.guide-config',
  '/ai/prompts': 'ai.guide-config',
  '/ai/records': 'ai.sessions',
  '/stats/overview': 'analytics.revenue',
  '/feedback': 'operations.work-orders',
  '/feedback/list': 'operations.work-orders',
  '/basic/feedback': 'operations.work-orders',
  '/basic/feedback/list': 'operations.work-orders',
}

const systemAccessPaths = new Set([
  '/system/user',
  '/system/role',
  '/system/dept',
  '/system/menu',
])

const adminMenus = ref<AdminMenuItem[]>([])
const removeDynamicRoutes: Array<() => void> = []
const registeredRoutePaths = new Set<string>()
let dynamicRoutesReady = false
let dynamicRoutesRequest: Promise<BackendRouter[]> | null = null

class UnauthorizedError extends Error {}

const withLeadingSlash = (path?: string) => {
  const value = String(path || '').trim()
  if (!value || value === '#') return ''
  return value.startsWith('/') ? value : `/${value}`
}

export const normalizeRoutePath = (path?: string) => {
  const normalized = withLeadingSlash(path)
  return routePathAliases[normalized] || normalized
}

const pageKeyFor = (rawPath: string, routePath: string) => {
  const alias = pageKeyAliases[withLeadingSlash(rawPath)]
  if (alias) return alias
  return routePath.replace(/^\/+/, '').replace(/\//g, '.')
}

const CP1252_BYTE_MAP: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f,
}

const mojibakeHintPattern = /[\u00c0-\u00ff\u2018-\u201d\u2026\u2030\ufffd]/

const countChineseChars = (value: string) => (String(value || '').match(/[\u4e00-\u9fa5]/g) || []).length

const decodeEntityCodePoint = (value: string, radix: number) => {
  const codePoint = Number.parseInt(value, radix)
  if (!Number.isFinite(codePoint)) return ''
  try {
    return String.fromCodePoint(codePoint)
  } catch {
    return ''
  }
}

const decodeTextEscapes = (value: string) => String(value || '')
  .replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => decodeEntityCodePoint(code, 16) || match)
  .replace(/&#x([0-9a-fA-F]+);/g, (match, code) => decodeEntityCodePoint(code, 16) || match)
  .replace(/&#(\d+);/g, (match, code) => decodeEntityCodePoint(code, 10) || match)

const decodeMojibakeText = (value: string) => {
  const text = String(value || '')
  if (!mojibakeHintPattern.test(text)) return text
  const bytes: string[] = []
  for (const char of text) {
    const code = char.charCodeAt(0)
    const byte = code <= 0xff ? code : CP1252_BYTE_MAP[code]
    if (byte === undefined) return text
    bytes.push(`%${byte.toString(16).padStart(2, '0')}`)
  }
  try {
    const decoded = decodeURIComponent(bytes.join(''))
    return countChineseChars(decoded) > countChineseChars(text) ? decoded : text
  } catch {
    return text
  }
}

const normalizeDisplayText = (value?: unknown) => {
  const text = String(value || '').trim()
  if (!text) return ''
  return decodeMojibakeText(decodeTextEscapes(text)
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")).trim()
}

const isMissingDisplayTitle = (value: string) => !value || value === '/' || value === '#'

const routeTitleFor = (route: BackendRouter, fallback: string) => {
  const metaTitle = normalizeDisplayText(route.meta?.title)
  if (!isMissingDisplayTitle(metaTitle)) return metaTitle
  if (normalizeRoutePath(route.path) === '/') return '工作台'

  const nameTitle = normalizeDisplayText(route.name)
  return isMissingDisplayTitle(nameTitle) ? fallback : nameTitle
}

const routeNameFor = (rawRoute: BackendRouter, routePath: string, index: number) => {
  const base = String(rawRoute.name || routePath || `route-${index}`)
    .replace(/[^\w-]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `Dynamic-${base || index}`
}

const componentName = (component?: string) => String(component || '').replace(/^\/+/, '')

const resolveComponent = (routePath: string, component?: string) => {
  if (routePath === '/dashboard' || componentName(component) === 'Dashboard/index') {
    return () => import('../views/Dashboard/index.vue')
  }
  if (routePath === '/question/catalogs') {
    return () => import('../views/Admin/QuestionCatalogs.vue')
  }
  if (routePath === '/question/import') {
    return () => import('../views/Admin/QuestionImport.vue')
  }
  if (routePath === '/ai-index/jobs') {
    return () => import('../views/Admin/AiIndexJobs.vue')
  }
  if (routePath === '/ai-index/questions') {
    return () => import('../views/Admin/AiQuestionIndexes.vue')
  }
  if (routePath === '/ai-index/knowledge') {
    return () => import('../views/Admin/AiKnowledge.vue')
  }
  if (routePath === '/ai-index/logs') {
    return () => import('../views/Admin/AiIndexLogs.vue')
  }
  if (systemAccessPaths.has(routePath)) {
    return () => import('../views/Admin/SystemAccessPage.vue')
  }
  return () => import('../views/Admin/ManagementPage.vue')
}

const isLeafRoute = (route: BackendRouter) => {
  const component = componentName(route.component)
  return Boolean(withLeadingSlash(route.path))
    && !route.children?.length
    && component !== 'Layout'
    && component !== 'ParentView'
    && component !== 'InnerLink'
}

const toMenuItems = (route: BackendRouter): AdminMenuItem[] => {
  if (route.hidden) return []

  const children = (route.children || [])
    .flatMap(toMenuItems)
  const routePath = normalizeRoutePath(route.path)

  if (children.length) {
    if (routePath === '/') {
      const dashboardChild = children.find((child) => child.router === '/dashboard' || child.title === '工作台') || children[0]
      const otherChildren = children.filter((child) => child !== dashboardChild)
      return [{
        title: routeTitleFor(route, dashboardChild.title || '工作台'),
        router: dashboardChild.router,
        icon: route.meta?.icon || dashboardChild.icon || 'Menu',
      }, ...otherChildren]
    }

    return [{
      title: routeTitleFor(route, '未命名菜单'),
      router: routePath || children[0].router,
      icon: route.meta?.icon || 'Menu',
      children,
    }]
  }

  if (!isLeafRoute(route)) return []

  return [{
    title: routeTitleFor(route, '未命名菜单'),
    router: routePath,
    icon: route.meta?.icon || 'Menu',
  }]
}

const collectLeafRoutes = (routes: BackendRouter[], output: RouteRecordRaw[] = []) => {
  routes.forEach((route, index) => {
    if (route.hidden) return

    const routePath = normalizeRoutePath(route.path)
    if (isLeafRoute(route) && routePath && !registeredRoutePaths.has(routePath)) {
      registeredRoutePaths.add(routePath)
      output.push({
        path: routePath,
        name: routeNameFor(route, routePath, output.length + index),
        component: resolveComponent(routePath, route.component),
        meta: {
          title: routeTitleFor(route, '未命名页面'),
          icon: route.meta?.icon || 'Menu',
          pageKey: pageKeyFor(String(route.path || ''), routePath),
          query: route.query,
        },
      })
    }

    if (route.children?.length) collectLeafRoutes(route.children, output)
  })
  return output
}

export const setupDynamicRoutes = (routers: BackendRouter[] = [], fresh = false) => {
  removeDynamicRoutes.splice(0).forEach((remove) => remove())
  registeredRoutePaths.clear()

  const nextMenus = routers
    .flatMap(toMenuItems)
  adminMenus.value = nextMenus

  collectLeafRoutes(routers).forEach((route) => {
    removeDynamicRoutes.push(router.addRoute('Layout', route))
  })
  if (fresh) {
    dynamicRoutesReady = true
    AdenLocalStorage.set(StorageUtil.STORAGE_KEY_ROUTERS, routers)
  } else if (!routers.length) {
    dynamicRoutesReady = false
  }
}

export const getAdminMenus = () => adminMenus

const getCachedRouters = (): BackendRouter[] => {
  const routers = AdenLocalStorage.get(StorageUtil.STORAGE_KEY_ROUTERS)
  return Array.isArray(routers) ? routers : []
}

const hydrateDynamicRoutesFromCache = () => {
  const routers = getCachedRouters()
  if (!routers.length) return false
  setupDynamicRoutes(routers, true)
  return true
}

const fetchRoutersFromApi = async () => {
  const baseURL = String(process.env.VUE_APP_API_TARGET || '').replace(/\/$/, '')
  const response = await fetch(`${baseURL}/get/routers`, {
    headers: {
      Authorization: `Bearer ${AdenLocalStorage.get(StorageUtil.STORAGE_KEY_TOKEN)}`,
    },
  })
  const result = await response.json().catch(() => null)
  if (response.status === 401 || result?.code === 401) {
    throw new UnauthorizedError(result?.msg || '登录已过期')
  }
  if (!response.ok) {
    throw new Error(result?.msg || '菜单加载失败')
  }
  if (result?.code && result.code !== 200) {
    throw new Error(result?.msg || '菜单加载失败')
  }
  return Array.isArray(result?.data) ? result.data : []
}

export const refreshDynamicRoutes = async () => {
  if (!dynamicRoutesRequest) {
    dynamicRoutesRequest = fetchRoutersFromApi()
  }
  try {
    const routers = await dynamicRoutesRequest
    setupDynamicRoutes(routers, true)
    return routers
  } finally {
    dynamicRoutesRequest = null
  }
}

const firstLeaf = (menus: AdminMenuItem[]): string | null => {
  for (const menu of menus) {
    if (menu.children?.length) {
      const child = firstLeaf(menu.children)
      if (child) return child
    } else if (menu.router) {
      return menu.router
    }
  }
  return null
}

export const firstAccessibleRoute = (): string | null => firstLeaf(adminMenus.value)

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Login',
    component: () => import('../views/Login/index.vue'),
    meta: { title: '登录' },
  },
  {
    path: '/layout',
    name: 'Layout',
    component: () => import('../layout/index.vue'),
    redirect: () => firstAccessibleRoute() || '/',
    children: [],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: () => firstAccessibleRoute() || '/',
  },
]

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
})

if (AdenLocalStorage.get(StorageUtil.STORAGE_KEY_TOKEN)) {
  hydrateDynamicRoutesFromCache()
} else {
  setupDynamicRoutes()
}

const ensureDynamicRoutes = async () => {
  if (dynamicRoutesReady) return
  if (hydrateDynamicRoutesFromCache()) return
  await refreshDynamicRoutes()
}

router.beforeEach(async (to) => {
  const token = AdenLocalStorage.get(StorageUtil.STORAGE_KEY_TOKEN)
  if (!token) {
    if (to.name === 'Login') return true
    return '/'
  }

  try {
    await ensureDynamicRoutes()
  } catch (error) {
    if (error instanceof UnauthorizedError) {
      AdenLocalStorage.clearAll()
    }
    setupDynamicRoutes([])
    if (to.name === 'Login') return true
    return '/'
  }

  if (to.name === 'Login') {
    return firstAccessibleRoute() || true
  }

  const routePath = normalizeRoutePath(to.path)
  if (routePath && routePath !== to.path) {
    return { path: routePath, query: to.query, hash: to.hash, replace: true }
  }

  if (to.path === '/layout') {
    return firstAccessibleRoute() || '/'
  }

  if (registeredRoutePaths.has(routePath)) {
    if (to.name === 'NotFound' || !to.matched.length) {
      return to.fullPath
    }
    return true
  }

  if (to.name === 'NotFound' || (to.path !== '/layout' && !to.matched.length)) {
    return firstAccessibleRoute() || '/'
  }
  return firstAccessibleRoute() || '/'
})

export default router
